import { useCallback } from "react";

import {
	getUniquePrices,
	getUniqueServiceLevels,
	getUniqueTransitDays,
} from "~/features/quote/utils/carrier-selection-utils.ts";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import type {
	CarrierFiltersType,
	FilterKey,
} from "~/shared/types/carrier-selection-types.ts";

import { LiabilityFilter } from "./components/liability-filter/liability-filter.tsx";
import { RateFilter } from "./components/rate-filter/rate-filter.tsx";
import { ServiceLevelFilter } from "./components/service-level-filter/service-level-filter.tsx";
import { TransitDaysFilter } from "./components/transit-days-filter/transit-days-filter.tsx";

const parsePriceValues = (prices: (string | number)[]): number[] => {
	return prices
		.map((price) => {
			if (typeof price === "number") {
				return price;
			}

			const parsed = Number.parseFloat(price);
			return Number.isFinite(parsed) ? parsed : null;
		})
		.filter((price): price is number => typeof price === "number");
};

const SMALL_RATE_FILTER_STEP = 100;
const RATE_FILTER_STEP = 1000;

const getRateFilterStep = (min: number | null, max: number | null): number => {
	if (typeof min !== "number" || typeof max !== "number") {
		return RATE_FILTER_STEP;
	}

	return max - min < RATE_FILTER_STEP
		? SMALL_RATE_FILTER_STEP
		: RATE_FILTER_STEP;
};

type CarrierFilterProps = {
	carrierPrices: CarrierPrice[];
	onFilterChange: <K extends FilterKey>(
		key: K,
		value: CarrierFiltersType[K],
	) => void;
};

export const CarrierFilters = ({
	carrierPrices,
	onFilterChange,
}: CarrierFilterProps) => {
	const priceValues = parsePriceValues(getUniquePrices(carrierPrices));
	const hasPrices = priceValues.length > 0;
	const priceMin = hasPrices ? Math.min(...priceValues) : null;
	const priceMax = hasPrices ? Math.max(...priceValues) : null;
	const priceStep = getRateFilterStep(priceMin, priceMax);
	const transitDays = getUniqueTransitDays(carrierPrices);
	const maxTransitDays = transitDays.length > 0 ? Math.max(...transitDays) : 0;

	const handlePriceChange = useCallback(
		(value: [number, number] | null) => {
			onFilterChange("currentPrice", value);
		},
		[onFilterChange],
	);
	const handleTransitDaysChange = useCallback(
		(value: number[]) => {
			onFilterChange("currentTransitDays", value);
		},
		[onFilterChange],
	);
	const handleLiabilityChange = useCallback(
		(value: string[]) => {
			onFilterChange("currentLiability", value);
		},
		[onFilterChange],
	);
	const handleServiceLevelChange = useCallback(
		(value: string[]) => {
			onFilterChange("currentServiceLevel", value);
		},
		[onFilterChange],
	);

	return (
		<>
			<div className={"filter-wrapper service-level-filter-wrapper"}>
				<ServiceLevelFilter
					data={getUniqueServiceLevels(carrierPrices)}
					onChange={handleServiceLevelChange}
				/>
			</div>
			<div className={"filter-wrapper transit-days-filter-wrapper"}>
				<TransitDaysFilter
					max={maxTransitDays}
					min={0}
					onChange={handleTransitDaysChange}
				/>
			</div>
			<div className={"filter-wrapper rate-filter-wrapper"}>
				<RateFilter
					max={priceMax}
					min={priceMin}
					onChange={handlePriceChange}
					step={priceStep}
				/>
			</div>
			<div className={"filter-wrapper liability-filter-wrapper"}>
				<LiabilityFilter
					carrierPrices={carrierPrices}
					onChange={handleLiabilityChange}
				/>
			</div>
			<div className={"filter-space"}></div>
		</>
	);
};
